import type { LineDiagramSpec, LineRow, RailCell, RailCurve } from './LineDiagram'

/**
 * Lays a rail diagram out into rows with one cell per rail.
 *
 * A rail is active between the row that opens it (`op: 'start'`, or the row
 * after a `split`) and the row that closes it (`op: 'end'`, a loop close, or
 * a `merge` into another rail). On every row an active rail that is not the
 * row's own shows `pass`, so a parallel branch keeps running alongside the
 * one being listed.
 *
 * Diagrams are hand-written code (`CityConfig.lineDiagrams`), so a step on a
 * rail that is not open, a rail opened twice or a rail left open at the end
 * throws — a broken table must fail its test, not render a broken list.
 */
export function layoutLineDiagram(spec: LineDiagramSpec): LineRow[] {
  const active = new Set<number>()
  let opening: number[] = []
  const rows: LineRow[] = []
  const fail = (message: string): never => {
    throw new Error(`line diagram: ${message}`)
  }
  const passCells = (): RailCell[] =>
    Array.from({ length: spec.rails }, (_, rail) => (active.has(rail) ? 'pass' : 'none'))
  const requireOpen = (rail: number, at: string): void => {
    if (!active.has(rail)) fail(`rail ${rail} not open at ${at}`)
  }

  for (const step of spec.steps) {
    for (const rail of opening) active.add(rail)
    opening = []

    if ('caption' in step) {
      rows.push({ kind: 'caption', caption: step.caption, cells: passCells() })
      continue
    }
    if ('loopClose' in step) {
      requireOpen(step.rail, step.loopClose)
      const cells = passCells()
      cells[step.rail] = 'end'
      rows.push({ kind: 'loopClose', slug: step.loopClose, cells })
      active.delete(step.rail)
      continue
    }

    if (step.op === 'start') {
      if (active.has(step.rail)) fail(`rail ${step.rail} already open at ${step.slug}`)
      active.add(step.rail)
    } else {
      requireOpen(step.rail, step.slug)
    }
    const cells = passCells()
    cells[step.rail] = step.op ?? 'stop'
    for (const rail of step.also ?? []) {
      requireOpen(rail, step.slug)
      cells[rail] = 'stop'
    }
    const curves: RailCurve[] = []
    for (const rail of step.merge ?? []) {
      requireOpen(rail, step.slug)
      cells[rail] = 'none'
      curves.push({ dir: 'in', from: rail, to: step.rail })
      active.delete(rail)
    }
    for (const rail of step.split ?? []) {
      if (active.has(rail)) fail(`rail ${rail} already open at ${step.slug}`)
      curves.push({ dir: 'out', from: step.rail, to: rail })
      opening.push(rail)
    }
    rows.push({ kind: 'station', slug: step.slug, cells, curves })
    if (step.op === 'end') active.delete(step.rail)
  }

  if (opening.length > 0) fail(`rail ${opening[0]} split off after the last row`)
  if (active.size > 0) fail(`rail ${[...active][0]} never closed`)
  return rows
}
